import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BeakerIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  LightBulbIcon,
  ArrowTrendingUpIcon,
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, ReferenceLine, Cell,
} from 'recharts';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import api from '../lib/api';
import { farmApi } from '../lib/api';
import { Card, CardHeader, CardTitle, Button, EmptyState, Modal, ModalFooter } from '../components/ui';
import { StatCardSkeleton, ChartSkeleton } from '../components/ui/Skeleton';

// ============ API HELPERS ============

interface SoilTest {
  id: string;
  farmId: string;
  labName?: string;
  sampleDate: string;
  sampleDepth?: number;
  pH: number;
  nitrogen: number;
  phosphorus: number;
  potassium: number;
  organicMatter: number;
  calcium?: number;
  magnesium?: number;
  sulfur?: number;
  iron?: number;
  zinc?: number;
  manganese?: number;
  copper?: number;
  boron?: number;
  sand?: number;
  silt?: number;
  clay?: number;
  cec?: number;
  electricalConductivity?: number;
  notes?: string;
  createdAt: string;
}

interface NutrientStatus {
  nutrient: string;
  value: number;
  unit: string;
  status: 'deficient' | 'low' | 'optimal' | 'high' | 'excessive';
  optimalRange: { min: number; max: number };
  color: string;
}

interface SoilAnalysis {
  overallScore: number;
  scoreLabel: string;
  scoreColor: string;
  nutrients: NutrientStatus[];
  phAssessment: { value: number; status: string; description: string; color: string };
  organicMatterAssessment: { value: number; status: string; description: string; color: string };
  textureClass: string | null;
  estimatedCEC: number | null;
  recommendations: string[];
}

interface NutrientRecommendation {
  nutrient: string;
  product: string;
  rateKgPerHa: number;
  timing: string;
  method: string;
  estimatedCostPerHa: number;
  environmentalNotes: string;
  priority: 'high' | 'medium' | 'low';
}

interface NutrientBalanceItem {
  nutrient: string;
  soilAvailable: number;
  cropRequirement: number;
  balance: number;
  unit: string;
  status: 'surplus' | 'adequate' | 'deficit';
}

interface TrendDataPoint {
  date: string;
  score: number;
  pH: number;
  organicMatter: number;
  nitrogen: number;
  phosphorus: number;
  potassium: number;
}

const soilApi = {
  getTests: (farmId: string) =>
    api.get<{ success: boolean; data: SoilTest[]; count: number }>(`/soil/tests/${farmId}`).then(r => r.data),
  getTestDetail: (testId: string) =>
    api.get<{ success: boolean; data: { test: SoilTest; analysis: SoilAnalysis } }>(`/soil/tests/detail/${testId}`).then(r => r.data),
  createTest: (data: Record<string, unknown>) =>
    api.post<{ success: boolean; data: SoilTest }>('/soil/tests', data).then(r => r.data),
  deleteTest: (testId: string) =>
    api.delete(`/soil/tests/${testId}`).then(r => r.data),
  getAnalysis: (farmId: string) =>
    api.get<{ success: boolean; data: { test: SoilTest; analysis: SoilAnalysis } }>(`/soil/analysis/${farmId}`).then(r => r.data),
  getRecommendations: (farmId: string, cropType: string) =>
    api.post<{ success: boolean; data: { recommendations: NutrientRecommendation[]; nutrientBalance: { nutrients: NutrientBalanceItem[] }; cropType: string } }>('/soil/recommendations', { farmId, cropType }).then(r => r.data),
  getTrends: (farmId: string) =>
    api.get<{ success: boolean; data: { farmId: string; dataPoints: number; trend: string; trendScore: number; history: TrendDataPoint[] } }>(`/soil/trends/${farmId}`).then(r => r.data),
  getNutrientBalance: (farmId: string, cropType: string) =>
    api.get<{ success: boolean; data: { nutrients: NutrientBalanceItem[] } }>(`/soil/nutrient-balance/${farmId}/${cropType}`).then(r => r.data),
  getCrops: () =>
    api.get<{ success: boolean; data: Array<{ id: string; label: string }> }>('/soil/crops').then(r => r.data),
};

// ============ TABS ============

const TABS = [
  { id: 'overview', label: 'Overview', icon: BeakerIcon },
  { id: 'tests', label: 'Tests', icon: ClipboardDocumentListIcon },
  { id: 'nutrients', label: 'Nutrients', icon: ChartBarIcon },
  { id: 'recommendations', label: 'Recommendations', icon: LightBulbIcon },
  { id: 'trends', label: 'Trends', icon: ArrowTrendingUpIcon },
] as const;

type TabId = typeof TABS[number]['id'];

// ============ STATUS BADGE ============

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    deficient: 'bg-red-100 text-red-700',
    low: 'bg-amber-100 text-amber-700',
    optimal: 'bg-green-100 text-green-700',
    high: 'bg-blue-100 text-blue-700',
    excessive: 'bg-red-100 text-red-700',
    surplus: 'bg-blue-100 text-blue-700',
    adequate: 'bg-green-100 text-green-700',
    deficit: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
}

// ============ PRIORITY BADGE ============

function PriorityBadge({ priority }: { priority: string }) {
  const colors: Record<string, string> = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-amber-100 text-amber-700',
    low: 'bg-green-100 text-green-700',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${colors[priority] || 'bg-gray-100 text-gray-700'}`}>
      {priority}
    </span>
  );
}

// ============ SCORE CIRCLE ============

function ScoreCircle({ score, label, color }: { score: number; label: string; color: string }) {
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-40 h-40">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 140 140">
          <circle cx="70" cy="70" r={radius} stroke="#e5e7eb" strokeWidth="10" fill="none" />
          <circle
            cx="70" cy="70" r={radius}
            stroke={color}
            strokeWidth="10"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-gray-900">{score}</span>
          <span className="text-xs text-gray-500">out of 100</span>
        </div>
      </div>
      <span className="mt-2 text-sm font-semibold" style={{ color }}>{label}</span>
    </div>
  );
}

// ============ ADD SOIL TEST MODAL ============

function AddSoilTestModal({
  isOpen,
  onClose,
  farmId,
}: {
  isOpen: boolean;
  onClose: () => void;
  farmId: string;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    sampleDate: new Date().toISOString().split('T')[0],
    labName: '',
    sampleDepth: '30',
    pH: '',
    nitrogen: '',
    phosphorus: '',
    potassium: '',
    organicMatter: '',
    calcium: '',
    magnesium: '',
    sulfur: '',
    iron: '',
    zinc: '',
    manganese: '',
    copper: '',
    boron: '',
    sand: '',
    silt: '',
    clay: '',
    cec: '',
    electricalConductivity: '',
    notes: '',
  });

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => soilApi.createTest(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['soil-tests'] });
      queryClient.invalidateQueries({ queryKey: ['soil-analysis'] });
      queryClient.invalidateQueries({ queryKey: ['soil-trends'] });
      toast.success('Soil test added successfully');
      onClose();
    },
    onError: () => {
      toast.error('Failed to add soil test');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: Record<string, unknown> = {
      farmId,
      sampleDate: form.sampleDate,
      pH: parseFloat(form.pH),
      nitrogen: parseFloat(form.nitrogen),
      phosphorus: parseFloat(form.phosphorus),
      potassium: parseFloat(form.potassium),
      organicMatter: parseFloat(form.organicMatter),
    };
    if (form.labName) payload.labName = form.labName;
    if (form.sampleDepth) payload.sampleDepth = parseFloat(form.sampleDepth);
    if (form.calcium) payload.calcium = parseFloat(form.calcium);
    if (form.magnesium) payload.magnesium = parseFloat(form.magnesium);
    if (form.sulfur) payload.sulfur = parseFloat(form.sulfur);
    if (form.iron) payload.iron = parseFloat(form.iron);
    if (form.zinc) payload.zinc = parseFloat(form.zinc);
    if (form.manganese) payload.manganese = parseFloat(form.manganese);
    if (form.copper) payload.copper = parseFloat(form.copper);
    if (form.boron) payload.boron = parseFloat(form.boron);
    if (form.sand) payload.sand = parseFloat(form.sand);
    if (form.silt) payload.silt = parseFloat(form.silt);
    if (form.clay) payload.clay = parseFloat(form.clay);
    if (form.cec) payload.cec = parseFloat(form.cec);
    if (form.electricalConductivity) payload.electricalConductivity = parseFloat(form.electricalConductivity);
    if (form.notes) payload.notes = form.notes;
    mutation.mutate(payload);
  };

  const updateField = (field: string, value: string) => setForm((prev) => ({ ...prev, [field]: value }));

  const inputClass = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20 transition-all';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Soil Test Result" size="full">
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto pr-1">
          {/* Basic info */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Sample Date *</label>
            <input type="date" required value={form.sampleDate} onChange={(e) => updateField('sampleDate', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Lab Name</label>
            <input type="text" value={form.labName} onChange={(e) => updateField('labName', e.target.value)} placeholder="e.g. KARI Lab" className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Sample Depth (cm)</label>
            <input type="number" step="any" value={form.sampleDepth} onChange={(e) => updateField('sampleDepth', e.target.value)} className={inputClass} />
          </div>

          {/* Core parameters */}
          <div className="sm:col-span-2 lg:col-span-3">
            <p className="text-sm font-semibold text-gray-700 mt-2 mb-1 border-b pb-1">Core Parameters (Required)</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">pH *</label>
            <input type="number" step="0.1" min="0" max="14" required value={form.pH} onChange={(e) => updateField('pH', e.target.value)} placeholder="e.g. 6.2" className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nitrogen (% total N) *</label>
            <input type="number" step="0.01" min="0" required value={form.nitrogen} onChange={(e) => updateField('nitrogen', e.target.value)} placeholder="e.g. 0.35" className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Phosphorus (ppm) *</label>
            <input type="number" step="0.1" min="0" required value={form.phosphorus} onChange={(e) => updateField('phosphorus', e.target.value)} placeholder="e.g. 28" className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Potassium (ppm) *</label>
            <input type="number" step="0.1" min="0" required value={form.potassium} onChange={(e) => updateField('potassium', e.target.value)} placeholder="e.g. 180" className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Organic Matter (%) *</label>
            <input type="number" step="0.1" min="0" required value={form.organicMatter} onChange={(e) => updateField('organicMatter', e.target.value)} placeholder="e.g. 4.2" className={inputClass} />
          </div>

          {/* Secondary nutrients */}
          <div className="sm:col-span-2 lg:col-span-3">
            <p className="text-sm font-semibold text-gray-700 mt-2 mb-1 border-b pb-1">Secondary & Micro Nutrients (Optional)</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Calcium (ppm)</label>
            <input type="number" step="0.1" min="0" value={form.calcium} onChange={(e) => updateField('calcium', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Magnesium (ppm)</label>
            <input type="number" step="0.1" min="0" value={form.magnesium} onChange={(e) => updateField('magnesium', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Sulfur (ppm)</label>
            <input type="number" step="0.1" min="0" value={form.sulfur} onChange={(e) => updateField('sulfur', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Iron (ppm)</label>
            <input type="number" step="0.1" min="0" value={form.iron} onChange={(e) => updateField('iron', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Zinc (ppm)</label>
            <input type="number" step="0.1" min="0" value={form.zinc} onChange={(e) => updateField('zinc', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Manganese (ppm)</label>
            <input type="number" step="0.1" min="0" value={form.manganese} onChange={(e) => updateField('manganese', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Copper (ppm)</label>
            <input type="number" step="0.1" min="0" value={form.copper} onChange={(e) => updateField('copper', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Boron (ppm)</label>
            <input type="number" step="0.1" min="0" value={form.boron} onChange={(e) => updateField('boron', e.target.value)} className={inputClass} />
          </div>

          {/* Texture */}
          <div className="sm:col-span-2 lg:col-span-3">
            <p className="text-sm font-semibold text-gray-700 mt-2 mb-1 border-b pb-1">Soil Texture & Other (Optional)</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Sand (%)</label>
            <input type="number" step="0.1" min="0" max="100" value={form.sand} onChange={(e) => updateField('sand', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Silt (%)</label>
            <input type="number" step="0.1" min="0" max="100" value={form.silt} onChange={(e) => updateField('silt', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Clay (%)</label>
            <input type="number" step="0.1" min="0" max="100" value={form.clay} onChange={(e) => updateField('clay', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">CEC (meq/100g)</label>
            <input type="number" step="0.1" min="0" value={form.cec} onChange={(e) => updateField('cec', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">EC (dS/m)</label>
            <input type="number" step="0.01" min="0" value={form.electricalConductivity} onChange={(e) => updateField('electricalConductivity', e.target.value)} className={inputClass} />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              rows={2}
              className={inputClass}
              placeholder="Additional observations..."
            />
          </div>
        </div>

        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
          <Button variant="primary" type="submit" loading={mutation.isPending}>Save Test</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ============ MAIN COMPONENT ============

export default function SoilHealth() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [selectedFarmId, setSelectedFarmId] = useState<string>('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCrop, setSelectedCrop] = useState('corn');

  // ── Farms ──
  const { data: farms = [], isLoading: farmsLoading } = useQuery({
    queryKey: ['farms'],
    queryFn: farmApi.list,
  });

  // Auto-select first farm
  const farmId = selectedFarmId || farms[0]?.id || '';

  // ── Soil Analysis (latest test) ──
  const { data: analysisData, isLoading: analysisLoading } = useQuery({
    queryKey: ['soil-analysis', farmId],
    queryFn: () => soilApi.getAnalysis(farmId),
    enabled: !!farmId,
  });

  // ── Soil Tests ──
  const { data: testsData, isLoading: testsLoading } = useQuery({
    queryKey: ['soil-tests', farmId],
    queryFn: () => soilApi.getTests(farmId),
    enabled: !!farmId,
  });

  // ── Recommendations ──
  const { data: recsData, isLoading: recsLoading } = useQuery({
    queryKey: ['soil-recs', farmId, selectedCrop],
    queryFn: () => soilApi.getRecommendations(farmId, selectedCrop),
    enabled: !!farmId && activeTab === 'recommendations',
  });

  // ── Trends ──
  const { data: trendsData, isLoading: trendsLoading } = useQuery({
    queryKey: ['soil-trends', farmId],
    queryFn: () => soilApi.getTrends(farmId),
    enabled: !!farmId && (activeTab === 'trends' || activeTab === 'overview'),
  });

  // ── Supported Crops ──
  const { data: cropsData } = useQuery({
    queryKey: ['soil-crops'],
    queryFn: soilApi.getCrops,
  });

  // ── Delete test ──
  const deleteMutation = useMutation({
    mutationFn: soilApi.deleteTest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['soil-tests'] });
      queryClient.invalidateQueries({ queryKey: ['soil-analysis'] });
      queryClient.invalidateQueries({ queryKey: ['soil-trends'] });
      toast.success('Soil test deleted');
    },
    onError: () => toast.error('Failed to delete soil test'),
  });

  const analysis = analysisData?.data?.analysis;
  const latestTest = analysisData?.data?.test;
  const tests = testsData?.data || [];
  const recommendations = recsData?.data?.recommendations || [];
  const nutrientBalance = recsData?.data?.nutrientBalance?.nutrients || [];
  const trendHistory = trendsData?.data?.history || [];
  const trendInfo = trendsData?.data;
  const supportedCrops = cropsData?.data || [];

  // Loading state
  if (farmsLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="h-8 w-48 skeleton-shimmer rounded" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
        </div>
      </div>
    );
  }

  if (farms.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          icon={BeakerIcon}
          title="No farms available"
          description="Add a farm first to start tracking soil health and getting nutrient recommendations."
          action={{ label: 'Go to Farms', href: '/farms' }}
        />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Soil Health</h1>
          <p className="text-gray-500 mt-1">Monitor soil conditions and get fertilizer recommendations</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={farmId}
            onChange={(e) => setSelectedFarmId(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20"
          >
            {farms.map((farm) => (
              <option key={farm.id} value={farm.id}>{farm.name}</option>
            ))}
          </select>
          <Button
            variant="primary"
            size="md"
            leftIcon={<PlusIcon className="h-5 w-5" />}
            onClick={() => setShowAddModal(true)}
          >
            Add Test
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-1 overflow-x-auto" aria-label="Tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors
                  ${activeTab === tab.id
                    ? 'border-kenagri-600 text-kenagri-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab
          analysis={analysis}
          latestTest={latestTest}
          trendInfo={trendInfo}
          loading={analysisLoading}
        />
      )}
      {activeTab === 'tests' && (
        <TestsTab
          tests={tests}
          loading={testsLoading}
          onAdd={() => setShowAddModal(true)}
          onDelete={(id) => {
            if (confirm('Delete this soil test?')) deleteMutation.mutate(id);
          }}
        />
      )}
      {activeTab === 'nutrients' && (
        <NutrientsTab
          analysis={analysis}
          latestTest={latestTest}
          loading={analysisLoading}
          farmId={farmId}
          selectedCrop={selectedCrop}
          onCropChange={setSelectedCrop}
          supportedCrops={supportedCrops}
        />
      )}
      {activeTab === 'recommendations' && (
        <RecommendationsTab
          recommendations={recommendations}
          nutrientBalance={nutrientBalance}
          loading={recsLoading}
          selectedCrop={selectedCrop}
          onCropChange={setSelectedCrop}
          supportedCrops={supportedCrops}
        />
      )}
      {activeTab === 'trends' && (
        <TrendsTab
          trendHistory={trendHistory}
          trendInfo={trendInfo}
          loading={trendsLoading}
        />
      )}

      {/* Add Soil Test Modal */}
      {showAddModal && (
        <AddSoilTestModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          farmId={farmId}
        />
      )}
    </div>
  );
}

// ============ OVERVIEW TAB ============

function OverviewTab({
  analysis,
  latestTest,
  trendInfo,
  loading,
}: {
  analysis?: SoilAnalysis;
  latestTest?: SoilTest;
  trendInfo?: { trend: string; trendScore: number; dataPoints: number } | null;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <ChartSkeleton height={200} />
        <ChartSkeleton height={200} />
        <ChartSkeleton height={200} />
      </div>
    );
  }

  if (!analysis || !latestTest) {
    return (
      <EmptyState
        icon={BeakerIcon}
        title="No soil tests yet"
        description="Add your first soil test to see health analysis and recommendations."
      />
    );
  }

  const macroNutrients = analysis.nutrients.filter((n) =>
    ['Nitrogen (N)', 'Phosphorus (P)', 'Potassium (K)'].includes(n.nutrient)
  );

  return (
    <div className="space-y-6">
      {/* Score + Key Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Score */}
        <Card className="flex flex-col items-center justify-center py-8">
          <ScoreCircle score={analysis.overallScore} label={analysis.scoreLabel} color={analysis.scoreColor} />
          <p className="text-xs text-gray-500 mt-3">
            Based on test from {format(new Date(latestTest.sampleDate), 'MMM d, yyyy')}
          </p>
          {trendInfo && trendInfo.dataPoints >= 2 && (
            <div className={`mt-2 flex items-center gap-1 text-xs font-medium ${
              trendInfo.trend === 'improving' ? 'text-green-600' : trendInfo.trend === 'degrading' ? 'text-red-600' : 'text-gray-500'
            }`}>
              <ArrowTrendingUpIcon className={`h-3.5 w-3.5 ${trendInfo.trend === 'degrading' ? 'rotate-180' : ''}`} />
              {trendInfo.trend === 'improving' ? 'Improving' : trendInfo.trend === 'degrading' ? 'Degrading' : 'Stable'} trend
            </div>
          )}
        </Card>

        {/* pH & OM */}
        <Card>
          <CardHeader>
            <CardTitle>pH & Organic Matter</CardTitle>
          </CardHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">pH</span>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: analysis.phAssessment.color + '20', color: analysis.phAssessment.color }}>
                  {analysis.phAssessment.status}
                </span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{analysis.phAssessment.value}</p>
              <p className="text-xs text-gray-500 mt-1">{analysis.phAssessment.description}</p>
            </div>
            <div className="p-3 rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">Organic Matter</span>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: analysis.organicMatterAssessment.color + '20', color: analysis.organicMatterAssessment.color }}>
                  {analysis.organicMatterAssessment.status}
                </span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{analysis.organicMatterAssessment.value}%</p>
              <p className="text-xs text-gray-500 mt-1">{analysis.organicMatterAssessment.description}</p>
            </div>
          </div>
        </Card>

        {/* Nutrient Status Cards */}
        <Card>
          <CardHeader>
            <CardTitle>Macro Nutrients</CardTitle>
          </CardHeader>
          <div className="space-y-3">
            {macroNutrients.map((n) => (
              <div key={n.nutrient} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                <div>
                  <p className="text-sm font-medium text-gray-700">{n.nutrient}</p>
                  <p className="text-lg font-bold text-gray-900">
                    {n.value} <span className="text-xs font-normal text-gray-500">{n.unit}</span>
                  </p>
                </div>
                <StatusBadge status={n.status} />
              </div>
            ))}
            {analysis.textureClass && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                <div>
                  <p className="text-sm font-medium text-gray-700">Soil Texture</p>
                  <p className="text-lg font-bold text-gray-900">{analysis.textureClass}</p>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Recommendations Summary */}
      {analysis.recommendations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Key Recommendations</CardTitle>
          </CardHeader>
          <div className="space-y-2">
            {analysis.recommendations.map((rec, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
                <LightBulbIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">{rec}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ============ TESTS TAB ============

function TestsTab({
  tests,
  loading,
  onAdd,
  onDelete,
}: {
  tests: SoilTest[];
  loading: boolean;
  onAdd: () => void;
  onDelete: (id: string) => void;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: detailData } = useQuery({
    queryKey: ['soil-test-detail', expandedId],
    queryFn: () => soilApi.getTestDetail(expandedId!),
    enabled: !!expandedId,
  });

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 skeleton-shimmer rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-32 skeleton-shimmer rounded" />
                <div className="h-3 w-48 skeleton-shimmer rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (tests.length === 0) {
    return (
      <EmptyState
        icon={ClipboardDocumentListIcon}
        title="No soil tests recorded"
        description="Add your first soil test result to start tracking soil health."
        action={{ label: 'Add Soil Test', onClick: onAdd }}
      />
    );
  }

  return (
    <div className="space-y-3">
      {tests.map((test) => {
        const isExpanded = expandedId === test.id;
        const detail = isExpanded ? detailData?.data : null;

        return (
          <div key={test.id} className="bg-white rounded-xl border border-gray-100 shadow-card overflow-hidden">
            {/* Row */}
            <button
              onClick={() => setExpandedId(isExpanded ? null : test.id)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-kenagri-100 flex items-center justify-center">
                  <BeakerIcon className="h-5 w-5 text-kenagri-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {format(new Date(test.sampleDate), 'MMM d, yyyy')}
                    {test.labName && <span className="text-gray-500 font-normal"> - {test.labName}</span>}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                    <span>pH: {test.pH}</span>
                    <span>N: {test.nitrogen}%</span>
                    <span>P: {test.phosphorus} ppm</span>
                    <span>K: {test.potassium} ppm</span>
                    <span>OM: {test.organicMatter}%</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(test.id); }}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
                <svg className={`h-5 w-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            {/* Expanded Detail */}
            {isExpanded && detail && (
              <div className="border-t border-gray-100 p-4 bg-gray-50">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {detail.analysis.nutrients.map((n) => (
                    <div key={n.nutrient} className="bg-white rounded-lg p-3 border border-gray-100">
                      <p className="text-xs text-gray-500">{n.nutrient}</p>
                      <p className="text-sm font-bold text-gray-900">{n.value} {n.unit}</p>
                      <StatusBadge status={n.status} />
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-4">
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-xs text-gray-500">Overall Score</p>
                    <p className="text-lg font-bold" style={{ color: detail.analysis.scoreColor }}>{detail.analysis.overallScore}/100</p>
                  </div>
                  {detail.analysis.textureClass && (
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                      <p className="text-xs text-gray-500">Texture</p>
                      <p className="text-sm font-bold text-gray-900">{detail.analysis.textureClass}</p>
                    </div>
                  )}
                  {detail.analysis.estimatedCEC && (
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                      <p className="text-xs text-gray-500">CEC</p>
                      <p className="text-sm font-bold text-gray-900">{detail.analysis.estimatedCEC} meq/100g</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============ NUTRIENTS TAB ============

function NutrientsTab({
  analysis,
  latestTest,
  loading,
  farmId,
  selectedCrop,
  onCropChange,
  supportedCrops,
}: {
  analysis?: SoilAnalysis;
  latestTest?: SoilTest;
  loading: boolean;
  farmId: string;
  selectedCrop: string;
  onCropChange: (crop: string) => void;
  supportedCrops: Array<{ id: string; label: string }>;
}) {
  const { data: balanceData } = useQuery({
    queryKey: ['soil-balance', farmId, selectedCrop],
    queryFn: () => soilApi.getNutrientBalance(farmId, selectedCrop),
    enabled: !!farmId,
  });

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartSkeleton height={300} />
        <ChartSkeleton height={300} />
      </div>
    );
  }

  if (!analysis || !latestTest) {
    return (
      <EmptyState
        icon={ChartBarIcon}
        title="No nutrient data"
        description="Add a soil test to view nutrient analysis."
      />
    );
  }

  const macroNutrients = analysis.nutrients.filter((n) =>
    ['Nitrogen (N)', 'Phosphorus (P)', 'Potassium (K)'].includes(n.nutrient)
  );
  const microNutrients = analysis.nutrients.filter((n) =>
    !['Nitrogen (N)', 'Phosphorus (P)', 'Potassium (K)'].includes(n.nutrient)
  );

  // Bar chart data for macro nutrients
  const macroChartData = macroNutrients.map((n) => ({
    name: n.nutrient.split(' ')[0],
    value: n.value,
    optMin: n.optimalRange.min,
    optMax: n.optimalRange.max,
    fill: n.color,
  }));

  const balanceNutrients = balanceData?.data?.nutrients || [];
  const balanceChartData = balanceNutrients.map((n) => ({
    name: n.nutrient.replace(/\(.+\)/, '').trim(),
    available: n.soilAvailable,
    required: n.cropRequirement,
    balance: n.balance,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Macro Nutrient Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Macro Nutrients</CardTitle>
          </CardHeader>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={macroChartData} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    `${value}`,
                    name === 'value' ? 'Measured' : name,
                  ]}
                />
                <Bar dataKey="value" name="Measured" radius={[4, 4, 0, 0]}>
                  {macroChartData.map((entry, index) => (
                    <Cell key={index} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-green-500 inline-block" /> Optimal
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-amber-500 inline-block" /> Low
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-red-700 inline-block" /> Deficient
            </span>
          </div>
        </Card>

        {/* Nutrient Balance */}
        <Card>
          <CardHeader>
            <CardTitle>Nutrient Balance</CardTitle>
            <select
              value={selectedCrop}
              onChange={(e) => onCropChange(e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm"
            >
              {supportedCrops.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </CardHeader>
          <div className="h-72">
            {balanceChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={balanceChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="available" name="Available (kg/ha)" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="required" name="Required (kg/ha)" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500 text-sm">
                No balance data available
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Micro Nutrients Table */}
      {microNutrients.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Secondary & Micro Nutrients</CardTitle>
          </CardHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Nutrient</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600">Value</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600">Optimal Range</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {microNutrients.map((n) => (
                  <tr key={n.nutrient} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium text-gray-900">{n.nutrient}</td>
                    <td className="py-3 px-4 text-right text-gray-900">{n.value} {n.unit}</td>
                    <td className="py-3 px-4 text-right text-gray-500">{n.optimalRange.min} - {n.optimalRange.max} {n.unit}</td>
                    <td className="py-3 px-4 text-center"><StatusBadge status={n.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ============ RECOMMENDATIONS TAB ============

function RecommendationsTab({
  recommendations,
  nutrientBalance,
  loading,
  selectedCrop,
  onCropChange,
  supportedCrops,
}: {
  recommendations: NutrientRecommendation[];
  nutrientBalance: NutrientBalanceItem[];
  loading: boolean;
  selectedCrop: string;
  onCropChange: (crop: string) => void;
  supportedCrops: Array<{ id: string; label: string }>;
}) {
  if (loading) {
    return (
      <div className="space-y-4">
        <ChartSkeleton height={200} />
        <ChartSkeleton height={200} />
      </div>
    );
  }

  const totalCost = recommendations.reduce((sum, r) => sum + r.estimatedCostPerHa, 0);

  return (
    <div className="space-y-6">
      {/* Crop selector */}
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Fertilizer Recommendations</h3>
            <p className="text-sm text-gray-500 mt-1">Crop-specific recommendations based on your latest soil test</p>
          </div>
          <select
            value={selectedCrop}
            onChange={(e) => onCropChange(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm focus:border-kenagri-500 focus:ring-2 focus:ring-kenagri-500/20"
          >
            {supportedCrops.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </div>
      </Card>

      {recommendations.length === 0 ? (
        <EmptyState
          icon={LightBulbIcon}
          title="No recommendations"
          description="Add a soil test first to get crop-specific fertilizer recommendations."
        />
      ) : (
        <>
          {/* Recommendations table */}
          <Card padding="none">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Nutrient</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Product</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Rate (kg/ha)</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Timing</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Method</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Cost (KES/ha)</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {recommendations.map((rec, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-gray-900">{rec.nutrient}</td>
                      <td className="py-3 px-4 text-gray-700">{rec.product}</td>
                      <td className="py-3 px-4 text-right font-semibold text-gray-900">{rec.rateKgPerHa.toLocaleString()}</td>
                      <td className="py-3 px-4 text-gray-600 text-xs max-w-[200px]">{rec.timing}</td>
                      <td className="py-3 px-4 text-gray-600 text-xs">{rec.method}</td>
                      <td className="py-3 px-4 text-right text-gray-900">{rec.estimatedCostPerHa.toLocaleString()}</td>
                      <td className="py-3 px-4 text-center"><PriorityBadge priority={rec.priority} /></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-semibold">
                    <td colSpan={5} className="py-3 px-4 text-gray-700">Total Estimated Cost</td>
                    <td className="py-3 px-4 text-right text-gray-900">KES {totalCost.toLocaleString()}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>

          {/* Nutrient balance summary */}
          {nutrientBalance.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Nutrient Balance Summary</CardTitle>
              </CardHeader>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {nutrientBalance.map((n) => (
                  <div
                    key={n.nutrient}
                    className={`p-4 rounded-xl border ${
                      n.status === 'deficit' ? 'bg-red-50 border-red-100' :
                      n.status === 'surplus' ? 'bg-blue-50 border-blue-100' :
                      'bg-green-50 border-green-100'
                    }`}
                  >
                    <p className="text-sm font-medium text-gray-700">{n.nutrient}</p>
                    <p className="text-xl font-bold text-gray-900 mt-1">
                      {n.balance > 0 ? '+' : ''}{n.balance} {n.unit}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-gray-500">Available: {n.soilAvailable}</span>
                      <StatusBadge status={n.status} />
                    </div>
                    <span className="text-xs text-gray-500">Required: {n.cropRequirement}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Environmental notes */}
          <Card>
            <CardHeader>
              <CardTitle>Environmental Considerations</CardTitle>
            </CardHeader>
            <div className="space-y-2">
              {recommendations.filter((r) => r.environmentalNotes).map((rec, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-100">
                  <ExclamationTriangleIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-blue-700">{rec.nutrient}</p>
                    <p className="text-sm text-gray-700">{rec.environmentalNotes}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ============ TRENDS TAB ============

function TrendsTab({
  trendHistory,
  trendInfo,
  loading,
}: {
  trendHistory: TrendDataPoint[];
  trendInfo?: { trend: string; trendScore: number; dataPoints: number } | null;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartSkeleton height={300} />
        <ChartSkeleton height={300} />
      </div>
    );
  }

  if (trendHistory.length === 0) {
    return (
      <EmptyState
        icon={ArrowTrendingUpIcon}
        title="Not enough data for trends"
        description="Add multiple soil tests over time to track soil health trends."
      />
    );
  }

  const chartData = trendHistory.map((d) => ({
    ...d,
    date: format(new Date(d.date), 'MMM yyyy'),
  }));

  const trendColor = trendInfo?.trend === 'improving' ? '#10b981' : trendInfo?.trend === 'degrading' ? '#ef4444' : '#6b7280';
  const trendLabel = trendInfo?.trend === 'improving' ? 'Improving' : trendInfo?.trend === 'degrading' ? 'Degrading' : 'Stable';

  return (
    <div className="space-y-6">
      {/* Trend indicator */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 px-4 py-2 rounded-xl border" style={{ borderColor: trendColor + '40', backgroundColor: trendColor + '10' }}>
          <ArrowTrendingUpIcon className={`h-5 w-5 ${trendInfo?.trend === 'degrading' ? 'rotate-180' : ''}`} style={{ color: trendColor }} />
          <span className="text-sm font-semibold" style={{ color: trendColor }}>
            {trendLabel} (score: {trendInfo?.trendScore?.toFixed(2)})
          </span>
        </div>
        <span className="text-sm text-gray-500">{trendInfo?.dataPoints} data points</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Health Score Trend */}
        <Card>
          <CardHeader>
            <CardTitle>Soil Health Score Trend</CardTitle>
          </CardHeader>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <Tooltip />
                <ReferenceLine y={60} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: 'Good', fill: '#f59e0b', fontSize: 10 }} />
                <Line type="monotone" dataKey="score" name="Health Score" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* pH Trend */}
        <Card>
          <CardHeader>
            <CardTitle>pH Trend</CardTitle>
          </CardHeader>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis domain={[4, 9]} tick={{ fontSize: 11 }} />
                <Tooltip />
                <ReferenceLine y={5.5} stroke="#f59e0b" strokeDasharray="5 5" />
                <ReferenceLine y={7.0} stroke="#f59e0b" strokeDasharray="5 5" />
                <Line type="monotone" dataKey="pH" name="pH" stroke="#8b5cf6" strokeWidth={2} dot={{ fill: '#8b5cf6', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Organic Matter Trend */}
        <Card>
          <CardHeader>
            <CardTitle>Organic Matter Trend</CardTitle>
          </CardHeader>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value: number) => [`${value}%`, 'OM']} />
                <ReferenceLine y={3.0} stroke="#f59e0b" strokeDasharray="5 5" />
                <ReferenceLine y={6.0} stroke="#f59e0b" strokeDasharray="5 5" />
                <Line type="monotone" dataKey="organicMatter" name="Organic Matter (%)" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* NPK Trends */}
        <Card>
          <CardHeader>
            <CardTitle>NPK Trends</CardTitle>
          </CardHeader>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="nitrogen" name="N (%)" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="phosphorus" name="P (ppm)" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="potassium" name="K (ppm)" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
